import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';
import { ROLENAME } from '../utils';

const CITY_HEADER_BG = '#1e3a8a';
const CITY_HEADER_FG = '#ffffff';
const EC_HEADER_BG = '#6b7280';
const EC_HEADER_FG = '#ffffff';
const MGR_ROW_BG = '#ffedd5';
const MGR_ROW_FG = '#000000';
const TABLE_TEXT_COLOR = '#000000';
const CELL_BORDER_COLOR = '#000000';
const CELL_BORDER = `0.5px solid ${CELL_BORDER_COLOR}`;
const DEFAULT_CELL_BG = '#ffffff';

const DESIGN_GRID_COLS = 3;

const CELL_TEXT_WRAP = {
  maxWidth: '100%',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

function gridCellBorders(colIndex, rowIndex, colCount, rowCount) {
  return {
    boxSizing: 'border-box',
    borderColor: CELL_BORDER_COLOR,
    borderRight: colIndex < colCount - 1 ? CELL_BORDER : undefined,
    borderBottom: rowIndex < rowCount - 1 ? CELL_BORDER : undefined,
    minWidth: 0,
  };
}

function hexToRgba(hex, alpha = 0.32) {
  const h = String(hex || '').trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(h)) return undefined;
  const n = parseInt(h.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function resolveRowStatus(row) {
  if (!row || typeof row !== 'object') return '';
  const nested = row.hrStatusColorCodeId || row.hrStatusColorCode;
  if (nested && typeof nested === 'object') {
    const fromCode = String(nested.status || '').trim();
    if (fromCode) return fromCode;
  }
  return String(row.status || row.rosterStatus || '').trim();
}

function resolveStatusKey(row) {
  if (!row) return '';
  const id =
    row.hrStatusColorCodeId?._id ||
    row.hrStatusColorCodeId ||
    row.hrStatusColorCode?._id ||
    row.hrStatusColorCode;
  if (id) return String(id);
  return resolveRowStatus(row);
}

function formatBracketStatusLabel(status) {
  const s = String(status || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s === 'active') return 'Active';
  if (s === 'ok') return 'Ok';
  if (s.includes('open req')) return 'Open Req';
  if (s.includes('yet to join')) return 'Yet to Join';
  if (s.includes('background verification failed')) return 'Background Verification Failed';
  if (s.includes('buffer') && s.includes('idd')) return 'Buffer (IDD)';
  return String(status).trim();
}

function shouldShowStatusInBracket(status) {
  return Boolean(String(status || '').trim());
}

/** Open Req, Yet to Join, Buffer (IDD) — show at end of each EC member grid */
function isLastGroupMemberStatus(row) {
  if (!row || typeof row !== 'object') return false;
  const st = resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim();
  const note = String(row.statusNote || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const check = (s) =>
    s.includes('open req') ||
    s.includes('yet to join') ||
    (s.includes('buffer') && s.includes('idd')) ||
    s.includes('buffer(idd)');
  return check(st) || check(note);
}

function sortMembersForDisplay(members) {
  const list = Array.isArray(members) ? members : [];
  const primary = [];
  const lastGroup = [];
  for (const member of list) {
    if (isLastGroupMemberStatus(member)) lastGroup.push(member);
    else primary.push(member);
  }
  return [...primary, ...lastGroup];
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function ordinalDay(n) {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return `${n}st`;
  if (j === 2 && k !== 12) return `${n}nd`;
  if (j === 3 && k !== 13) return `${n}rd`;
  return `${n}th`;
}

function isInvalidJoiningYear(y) {
  return !y || y <= 1970 || y < 1980;
}

function formatJoiningFromYmd(y, m, day) {
  if (!m || !day || isInvalidJoiningYear(y)) return '';
  return `${ordinalDay(day)} ${MONTHS_SHORT[m - 1]} ${y}`;
}

/** Parse API ISO strings using local calendar date (avoids IST off-by-one day). */
function parseJoiningDateParts(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    if (isInvalidJoiningYear(y)) return null;
    return { y, m, day };
  }
  const parsed =
    value instanceof Date
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? new Date(value)
        : new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  if (isInvalidJoiningYear(y)) return null;
  return { y, m: parsed.getMonth() + 1, day: parsed.getDate() };
}

function formatJoining(value) {
  const parts = parseJoiningDateParts(value);
  if (!parts) return '';
  return formatJoiningFromYmd(parts.y, parts.m, parts.day);
}

function resolveStatusDateForRow(row) {
  if (!row || typeof row !== 'object') return null;
  const s = resolveRowStatus(row).toLowerCase().replace(/\s+/g, ' ').trim();
  if (s.includes('open req')) return row.openReqDate ?? null;
  if (s === 'relieved') return row.relievedDate ?? null;
  if (s === 'transferred') return row.transferredDate ?? null;
  if (s === 'terminated') return row.terminatedDate ?? null;
  if (s === 'maternity') return row.maternityDate ?? null;
  if (s === 'industry') return row.industryDate ?? null;
  if (s === 'abscond') return row.abscondDate ?? null;
  if (s === 'resigned') return row.resignedDate ?? null;
  if (s === 'warning') return row.warningDate ?? null;
  if ((s.includes('buffer') && s.includes('idd')) || s.includes('buffer(idd)')) {
    return row.bufferIddDate ?? null;
  }
  if (s === 'active - training' || (s.includes('active') && s.includes('training'))) {
    return row.activeTrainingDate ?? null;
  }
  if (s === 'pip') return row.pipDate ?? null;
  return null;
}

function resolveRawJoiningDate(row) {
  if (!row || typeof row !== 'object') return null;
  return (
    row.joiningDate ??
    row.joining_date ??
    row.dateOfJoining ??
    row.date_of_joining ??
    row.doj ??
    row.joinDate ??
    row.joinedOn ??
    row.user?.joiningDate ??
    row.employee?.joiningDate ??
    row.rosterUser?.joiningDate ??
    null
  );
}

function isJoiningDateOnlyStatus(normalized) {
  return (
    normalized === 'yet to join' ||
    normalized === 'active' ||
    (normalized.includes('active') && normalized.includes('training'))
  );
}

function getPersonDateLines(person) {
  if (!person || typeof person !== 'object') {
    return { statusDate: '', joiningDate: '' };
  }
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (isJoiningDateOnlyStatus(normalized)) {
    return { statusDate: formatJoining(resolveRawJoiningDate(person)), joiningDate: '' };
  }
  return { statusDate: formatJoining(resolveStatusDateForRow(person)), joiningDate: '' };
}

function getPersonDisplayName(person) {
  if (!person || typeof person !== 'object') return '—';
  const name = String(person.name || person.employeeName || '').trim();
  if (name) return name;
  const normalized = resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim();
  if (normalized.includes('open req')) return 'Open Req';
  return '—';
}

function isOpenReqStatusPerson(person) {
  if (!person || typeof person !== 'object') return false;
  return resolveRowStatus(person).toLowerCase().replace(/\s+/g, ' ').trim().includes('open req');
}

function getPersonNameLine(person) {
  const displayName = getPersonDisplayName(person);
  const hasRealName = Boolean(String(person?.name || person?.employeeName || '').trim());
  if (isOpenReqStatusPerson(person) && !hasRealName) {
    return displayName;
  }
  return `${displayName}${statusSuffixBracket(person)}`;
}

/** Status in brackets after date, e.g. "Sujhata - 20th Oct 2024 (Warning)" or "(Open Req)" */
function statusSuffixBracket(row) {
  if (!row || typeof row !== 'object') return '';
  const st = resolveRowStatus(row);
  const note = String(row.statusNote || '').trim();
  const risk = String(row.atRiskCategory || '').trim();

  if (shouldShowStatusInBracket(st)) {
    return ` (${formatBracketStatusLabel(st)})`;
  }

  if (!st && shouldShowStatusInBracket(note)) {
    return ` (${formatBracketStatusLabel(note)})`;
  }

  if (shouldShowStatusInBracket(risk)) {
    return ` (${formatBracketStatusLabel(risk)})`;
  }

  if (shouldShowStatusInBracket(note)) {
    return ` (${formatBracketStatusLabel(note)})`;
  }

  return '';
}

function statusBackgroundOr(row, getStatusCellBackground, fallbackBg) {
  if (!row || typeof row !== 'object') return fallbackBg;
  const bg = getStatusCellBackground(row);
  return bg && bg !== DEFAULT_CELL_BG ? bg : fallbackBg;
}

function formatOdmLine(person) {
  if (!person) return 'ODM: —';
  if (typeof person === 'string') return `ODM: ${person}`;
  const displayName = getPersonDisplayName(person);
  if (displayName === '—') return 'ODM: —';
  const { statusDate, joiningDate } = getPersonDateLines(person);
  const dateParts = [statusDate, joiningDate].filter(Boolean);
  const datePart = dateParts.join(' / ');
  const hasRealName = Boolean(String(person.name || person.employeeName || '').trim());
  const statusSuffix =
    isOpenReqStatusPerson(person) && !hasRealName ? '' : statusSuffixBracket(person);
  const core = datePart
    ? `${displayName} - ${datePart}${statusSuffix}`
    : `${displayName}${statusSuffix}`;
  return `ODM: ${core}`;
}

function PersonCellContent({ person, prefix }) {
  if (!person) return null;
  if (typeof person === 'string') {
    const label = prefix ? `${prefix}: ${person}` : person;
    return (
      <Typography
        component="span"
        style={{
          fontSize: 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: prefix ? 700 : 400,
          lineHeight: 1.35,
          textAlign: 'center',
          display: 'block',
          ...CELL_TEXT_WRAP,
        }}
      >
        {label}
      </Typography>
    );
  }

  const nameLine = getPersonNameLine(person);
  const { statusDate, joiningDate } = getPersonDateLines(person);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      style={{ gap: 2, width: '100%', boxSizing: 'border-box' }}
    >
      <Typography
        component="span"
        style={{
          fontSize: 14,
          color: TABLE_TEXT_COLOR,
          fontWeight: prefix ? 700 : 400,
          lineHeight: 1.35,
          textAlign: 'center',
          display: 'block',
          ...CELL_TEXT_WRAP,
        }}
      >
        {prefix ? `${prefix}: ${nameLine}` : nameLine}
      </Typography>
      {statusDate ? (
        <Typography
          component="span"
          style={{
            fontSize: 12,
            color: TABLE_TEXT_COLOR,
            lineHeight: 1.25,
            textAlign: 'center',
            display: 'block',
            ...CELL_TEXT_WRAP,
          }}
        >
          {statusDate}
        </Typography>
      ) : null}
      {joiningDate ? (
        <Typography
          component="span"
          style={{
            fontSize: 12,
            color: TABLE_TEXT_COLOR,
            lineHeight: 1.25,
            textAlign: 'center',
            display: 'block',
            ...CELL_TEXT_WRAP,
          }}
        >
          {joiningDate}
        </Typography>
      ) : null}
    </Box>
  );
}

function isManagerRow(role) {
  const r = String(role || '').toLowerCase();
  if (!r) return false;
  if (r.includes('asst') || r.includes('assistant') || r.includes('deputy')) return false;
  return r.includes('mgr') || r.includes('manager') || r === 'ch' || r.includes('head');
}

/**
 * Normalize API payload into { cities: [{ city, ecs: [{ ec, odmLine, members: [] }] }] }
 * Supports:
 * - cities[].locations[] with higherManagers + designUsers (design-hierarchy API)
 * - cities[].experienceCenters | ecs | children (legacy)
 * - flat rows[] fallback
 */
function buildHierarchyView(apiData) {
  if (!apiData || typeof apiData !== 'object') return { cities: [] };

  if (Array.isArray(apiData.cities)) {
    return {
      cities: apiData.cities
        .map((c) => {
          const cityName = c.city || c.cityName || '—';

          if (Array.isArray(c.locations) && c.locations.length > 0) {
            const ecs = c.locations
              .map((loc) => {
                const hm = Array.isArray(loc.higherManagers) ? loc.higherManagers : [];
                const first = hm[0];
                const members = Array.isArray(loc.designUsers)
                  ? loc.designUsers
                  : Array.isArray(loc.members)
                    ? loc.members
                    : Array.isArray(loc.rows)
                      ? loc.rows
                      : [];
                return {
                  ec: loc.ec || loc.experienceCenter || loc.ecName || '—',
                  odmPerson: first || null,
                  odmLine: formatOdmLine(first),
                  members,
                };
              })
              .filter((ecBlock) => Boolean(ecBlock.odmPerson));
            ecs.sort((a, b) => String(a.ec).localeCompare(String(b.ec), undefined, { sensitivity: 'base' }));
            return { city: cityName, ecs };
          }

          return {
            city: cityName,
            ecs: (c.experienceCenters || c.ecs || c.children || [])
              .map((e) => {
                const odmPerson =
                  (typeof e.odm === 'object' && e.odm) ||
                  (typeof e.manager === 'object' && e.manager) ||
                  (typeof e.mgr === 'object' && e.mgr) ||
                  (e.managerName || e.odmName
                    ? {
                        name: e.odmName || e.managerName,
                        joiningDate:
                          e.odmJoiningDate ||
                          e.managerJoiningDate ||
                          e.joiningDate ||
                          e.joining_date,
                      }
                    : null);
                return {
                  ec: e.ec || e.name || e.experienceCenter || '—',
                  odmPerson,
                  odmLine:
                    e.odmLabel ||
                    e.odmLine ||
                    e.managerLabel ||
                    e.mgrLine ||
                    formatOdmLine(odmPerson) ||
                    'ODM: —',
                  members: Array.isArray(e.members) ? e.members : Array.isArray(e.rows) ? e.rows : [],
                };
              })
              .filter((ecBlock) => Boolean(ecBlock.odmPerson)),
          };
        })
        .filter((cityBlock) => cityBlock.ecs.length > 0),
    };
  }

  if (Array.isArray(apiData.hierarchy)) {
    return buildHierarchyView({ cities: apiData.hierarchy });
  }

  const rows = Array.isArray(apiData.rows)
    ? apiData.rows
    : Array.isArray(apiData.items)
      ? apiData.items
      : Array.isArray(apiData)
        ? apiData
        : [];

  if (!rows.length) return { cities: [] };

  const tree = new Map();
  for (const row of rows) {
    const city = row.city || row.cityName || '—';
    const ec = row.ec || row.experienceCenter || row.ecName || '—';
    if (!tree.has(city)) tree.set(city, new Map());
    const ecMap = tree.get(city);
    if (!ecMap.has(ec)) {
      ecMap.set(ec, { managers: [], members: [] });
    }
    const bucket = ecMap.get(ec);
    const role = row.role || row.designation || '';
    if (isManagerRow(role)) bucket.managers.push(row);
    else bucket.members.push(row);
  }

  const cities = [];
  for (const [city, ecMap] of tree) {
    const ecs = [];
    for (const [ec, b] of ecMap) {
      ecs.push({
        ec,
        odmPerson: b.managers[0] || null,
        odmLine: formatOdmLine(b.managers[0]),
        members: b.members,
      });
    }
    ecs.sort((a, b) => String(a.ec).localeCompare(String(b.ec)));
    cities.push({ city, ecs });
  }
  return { cities };
}

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function HrRacksheetDesignHierarchy(props) {
  const { userDetails } = useSelector((state) => state.user);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hrStatusColorCodes, setHrStatusColorCodes] = useState([]);

  const loadHrStatusColorCodes = async () => {
    try {
      const response = await HttpService.getHrStatusColorCodes();
      const list = Array.isArray(response?.data) ? response.data : [];
      setHrStatusColorCodes(list);
    } catch (error) {
      console.error('Error loading HR status color codes:', error);
      setHrStatusColorCodes([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterDesignHierarchy();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load design hierarchy',
          'error'
        );
        setPayload(null);
        return;
      }
      setPayload(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading design hierarchy:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading design hierarchy',
        'error'
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHrStatusColorCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusCellBackground = useMemo(() => {
    return (row) => {
      const key = resolveStatusKey(row);
      if (!key) return DEFAULT_CELL_BG;
      const normalizedKey = key.toLowerCase();
      const hit = hrStatusColorCodes.find(
        (item) =>
          String(item?._id) === key ||
          String(item?.status || '').trim().toLowerCase() === normalizedKey
      );
      if (!hit?.hexCode) return DEFAULT_CELL_BG;
      return hexToRgba(hit.hexCode, 0.32) || hit.hexCode;
    };
  }, [hrStatusColorCodes]);

  const view = useMemo(() => {
    const built = buildHierarchyView(payload);
    const hasFullAccess = userDetails?.roles?.some(
      (role) =>
        role?.name === ROLENAME.Admin ||
        role?.name === ROLENAME.HRUser ||
        role?.name === ROLENAME.HRManager ||
        role?.name === ROLENAME.HRAssistantManager
    );
    if (hasFullAccess) return built;
    const userCityNames = (userDetails?.locationId || [])
      .map((loc) => loc?.name)
      .filter(Boolean);
    return { cities: built.cities.filter((cityBlock) => userCityNames.includes(cityBlock.city)) };
  }, [payload, userDetails]);
  const title =
    (payload && payload.title) || 'Design hierarchy — city & experience center';

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }} mb={2}>
        <Typography variant="h5" style={{ fontWeight: 700, color: CITY_HEADER_BG }}>
          {title}
        </Typography>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={() => {
            load();
            loadHrStatusColorCodes();
          }}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      {!loading && view.cities.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No hierarchy data. If the API returns a different shape, extend buildHierarchyView in
          hrRacksheetDesignHierarchy.js.
        </Typography>
      )}

      {!loading && view.cities.length > 0 && (
        <Box style={{ width: '100%', overflowX: 'auto' }}>
          <Box
            display="flex"
            flexWrap="nowrap"
            alignItems="flex-start"
            style={{ gap: 16, minWidth: 'min-content' }}
          >
            {view.cities.map((cityBlock, cityIdx) => (
              <Box
                key={`city-col-${cityIdx}-${cityBlock.city}`}
                display="flex"
                flexDirection="column"
                style={{
                  flex: '0 0 auto',
                  minWidth: 280,
                  width: 380,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  py={1.2}
                  px={2}
                  style={{
                    backgroundColor: CITY_HEADER_BG,
                    color: CITY_HEADER_FG,
                    fontWeight: 700,
                    textAlign: 'center',
                    border: CELL_BORDER,
                    borderColor: CELL_BORDER_COLOR,
                  }}
                >
                  {cityBlock.city}
                </Box>

                {cityBlock.ecs.map((ecBlock) => (
                  <Box
                    key={`${cityBlock.city}-${ecBlock.ec}`}
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    style={{ boxSizing: 'border-box', flexShrink: 0 }}
                  >
                    <Box
                      py={1}
                      px={2}
                      style={{
                        backgroundColor: EC_HEADER_BG,
                        color: EC_HEADER_FG,
                        fontWeight: 700,
                        textAlign: 'center',
                        border: CELL_BORDER,
                        borderTop: 'none',
                        borderColor: CELL_BORDER_COLOR,
                        width: '100%',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                      }}
                    >
                      {ecBlock.ec}
                    </Box>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      py={1.5}
                      px={2}
                      style={{
                        backgroundColor: statusBackgroundOr(
                          ecBlock.odmPerson,
                          getStatusCellBackground,
                          MGR_ROW_BG
                        ),
                        color: MGR_ROW_FG,
                        fontWeight: 700,
                        textAlign: 'center',
                        border: CELL_BORDER,
                        borderTop: 'none',
                        borderColor: CELL_BORDER_COLOR,
                        width: '100%',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        minHeight: 'fit-content',
                        overflow: 'visible',
                      }}
                    >
                      {ecBlock.odmPerson ? (
                        <PersonCellContent person={ecBlock.odmPerson} prefix="ODM" />
                      ) : (
                        ecBlock.odmLine
                      )}
                    </Box>

                    <Box
                      style={{
                        border: CELL_BORDER,
                        borderTop: 'none',
                        borderColor: CELL_BORDER_COLOR,
                        width: '100%',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${DESIGN_GRID_COLS}, minmax(0, 1fr))`,
                      }}
                    >
                      {(() => {
                        const members = sortMembersForDisplay(ecBlock.members);
                        const rowsOf3 = chunkArray(members, DESIGN_GRID_COLS);
                        if (rowsOf3.length === 0) {
                          return (
                            <Box
                              style={{
                                gridColumn: '1 / -1',
                                padding: '16px 8px',
                                textAlign: 'center',
                                color: TABLE_TEXT_COLOR,
                                boxSizing: 'border-box',
                              }}
                            >
                              No team members in this EC
                            </Box>
                          );
                        }
                        return rowsOf3.flatMap((triplet, ri) =>
                          [0, 1, 2].map((ci) => {
                            const member = triplet[ci];
                            const key = member?._id
                              ? String(member._id)
                              : `cell-${cityBlock.city}-${ecBlock.ec}-${ri}-${ci}`;
                            return (
                              <Box
                                key={key}
                                py={1.5}
                                px={0.5}
                                style={{
                                  ...gridCellBorders(ci, ri, DESIGN_GRID_COLS, rowsOf3.length),
                                  backgroundColor: member
                                    ? getStatusCellBackground(member)
                                    : '#fafafa',
                                  color: TABLE_TEXT_COLOR,
                                  fontSize: 14,
                                  textAlign: 'center',
                                  minHeight: 52,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {member ? <PersonCellContent person={member} /> : '\u00a0'}
                              </Box>
                            );
                          })
                        );
                      })()}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetDesignHierarchy);
